/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from "react-i18next";

import { useCrudClickToEdit } from "@ryde/redux";
import {
  HANDLING_COLUMN_NAME,
  IColumnDefWithId,
  ID_COLUMN_NAME,
  ID_EMPTY_COLUMN_NAME,
  IRideTableDto,
  isGroupingValueDelayEnum,
  ITableMeta,
  LAST_COLUMN_LIVE_MAP,
  RideDelayStatusEnum,
  UTF_SPACE,
} from "@ryde/shared";

import { rideStatusDelayToColorLeftLine } from "../../../utils";
import { CellMultiIconRides, HeaderSimpleRides } from "../../cell-templates";
import { CellMultiIconRideMap } from "../../cell-templates/rides/live-map/cell.multi-icon.ride-map";
import { CellLiveMapRowActions } from "../../cell-templates/rides/live-map/cell.multi-icon.rides.live-map";
import { IdColumnEditGeneric } from "./cell.generic.id-column-edit";
import { GenericSortableCell } from "./cell.generic.sortable";
import { MultiIconHeader } from "./head-cell.generic.multi-icon";

export const colHandlingRidesObj: IColumnDefWithId<IRideTableDto, unknown> = {
  id: HANDLING_COLUMN_NAME,
  header: ({ table }) => (
    <MultiIconHeader dataIds={table.options.data?.map(x => x.id)} />
  ),
  cell: ({ row }) => <CellMultiIconRides row={row} />,
  meta: {
    orderable: false,
    fixed: true,
    columnWidth: 182,
    dataAttributeField: "id",
  },
};

export const colHandlingRideMapObj: IColumnDefWithId<IRideTableDto, unknown> = {
  id: HANDLING_COLUMN_NAME,
  header: ({ table }) => (
    <MultiIconHeader dataIds={table.options.data?.map(x => x.id)} />
  ),
  cell: ({ row }) => <CellMultiIconRideMap row={row} />,
  meta: {
    orderable: false,
    fixed: true,
    columnWidth: 108,
    dataAttributeField: "id",
  },
};

export const lastColLiveMapObj = (): IColumnDefWithId<IRideTableDto, any> => ({
  id: LAST_COLUMN_LIVE_MAP,
  header: () => <DefaultLiveMapLastActionsCol />,
  cell: ({ row }) => <CellLiveMapRowActions row={row} />,
  meta: {
    classNameHeader: "last_column_custom",
    columnWidth: 52,
    orderable: false,
    last: true,
  },
});

export const idEmptyColumnObjLiveMap = (): IColumnDefWithId<
  IRideTableDto,
  any
> => {
  return {
    id: ID_EMPTY_COLUMN_NAME,
    header: ctx => {
      const tableMeta = ctx.table.options.meta as ITableMeta;
      const groupingField = tableMeta.groupingField;
      const groupingValue = tableMeta.groupingValue;
      const isGroupedValueDelay = isGroupingValueDelayEnum(
        groupingField,
        groupingValue,
      );
      return (
        isGroupedValueDelay && (
          <span
            className={rideStatusDelayToColorLeftLine(
              groupingValue as RideDelayStatusEnum,
            )}
          ></span>
        )
      );
    },
    cell: ({ row }) => (
      <span
        className={rideStatusDelayToColorLeftLine(row.original.statusLate)}
      ></span>
    ),
    meta: {
      classNameHeader: "col_idempty custom_left_line_head",
      classNameCell: "custom_left_line",
      fixed: true,
    },
  };
};

export const useIdColumnLiveMapObj = (): IColumnDefWithId<
  IRideTableDto,
  any
> => {
  const { handleCrudClickToEdit } = useCrudClickToEdit();

  return {
    id: ID_COLUMN_NAME,
    header: () => <DefaultIDHeader />,
    cell: ({ row }) => (
      <IdColumnEditGeneric
        id={row?.original?.id || 0}
        key={row?.original?.id || 0}
        onEditLinkClick={handleCrudClickToEdit}
      />
    ),
    meta: {
      displayName: "Id",
      fixed: true,
    },
  };
};

export const DefaultLiveMapLastActionsCol = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <HeaderSimpleRides>{t("rideMap.gridLabel.rowActions")}</HeaderSimpleRides>
  );
};

export const DefaultIDHeader = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <GenericSortableCell fieldNameResolved={"id"}>
      {UTF_SPACE} {t("general.ID")}
    </GenericSortableCell>
  );
};
